import { lazy, Suspense, type ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AuthShell, Shell } from '@/components/Shell';
import { AuthGuard } from '@/components/AuthGuard';
import { PlatformRoute } from '@/components/PlatformRoute';
import { ParaATelaDoCaso } from '@/components/Casos/ParaATelaDoCaso';
import { CertificadorRoute } from '@/components/CertificadorRoute';
import { PortalGuard } from '@/components/Portal/PortalGuard';
import {
  AberturaEletronicaPage,
  AcessoPorQrPage,
  AdminClientesPage,
  AdminTrilhaPage,
  AdminUsuariosPage,
  AnalisePage,
  AvisosDaEquipePage,
  ArquivoDaPecaPage,
  ApiKeysPage,
  CasoDestinoPage,
  CasosPage,
  CertificacaoPage,
  ConfigPage,
  ConversaDoCasoPage,
  DossiePage,
  EscritorioPage,
  ForbiddenPage,
  CertificadosECarimboPage,
  HistoricoDoCasoPage,
  HomePage,
  InformacoesDaLeituraPage,
  LaudoCasoPage,
  MesaUnicaPage,
  LoginPage,
  MembersPage,
  NotFoundPage,
  NovoCasoPage,
  OnboardingPage,
  PerfilPage,
  PreAnalisePage,
  PrecificacaoPage,
  ResetPasswordPage,
  ResultadoDoCasoPage,
  OrientacoesDoCasoPage,
  ConferenciaDasSaidasPage,
  ConferenciaFinalPage,
  ServerErrorPage,
  SignupPage,
  TrocarSenhaPage,
  WorkspaceCasoPage,
} from '@/pages';

// CaseWizardPage é carregada lazy porque o bundle de Fabric.js (E8.F4)
// entra pela mesma página — mantemos o code-splitting.
const CaseWizardPage = lazy(async () => {
  const mod = await import('./pages/CaseWizardPage');
  return { default: mod.CaseWizardPage };
});

// Portal do Cliente (E50.F2): chunk lazy próprio — shell, tema e páginas do
// portal só baixam quando um usuário de portal navega para /portal/*.
const PortalRoutes = lazy(async () => {
  const mod = await import('./pages/portal/PortalRoutes');
  return { default: mod.PortalRoutes };
});

export interface AppRoutesProps {
  version?: string;
}

function ShellRoute({
  children,
  fullBleed,
  guarded = true,
}: {
  children: ReactNode;
  fullBleed?: boolean;
  guarded?: boolean;
}) {
  const inner = <Shell {...(fullBleed ? { fullBleed: true } : {})}>{children}</Shell>;
  // E50.F2: PortalGuard area="perito" devolve usuários de portal (ambiente
  // escritorio/banco) para /portal/casos. Fail-open: loading/erro/interno
  // renderizam como sempre — zero mudança para o legado.
  return guarded ? (
    <AuthGuard>
      <PortalGuard area="perito">{inner}</PortalGuard>
    </AuthGuard>
  ) : (
    inner
  );
}

/** Rota da área do perito no shell guardado. Chamada como função: `<Routes>` só lê `<Route>`. */
function rotaNoShell(path: string, pagina: ReactNode): JSX.Element {
  return <Route path={path} element={<ShellRoute>{pagina}</ShellRoute>} />;
}

export function AppRoutes(_props: AppRoutesProps = {}) {
  void _props;
  return (
    <Routes>
      {/* Pré-auth: layout centralizado, sem chrome */}
      <Route
        path="/login"
        element={
          <AuthShell>
            <LoginPage />
          </AuthShell>
        }
      />
      <Route
        path="/signup"
        element={
          <AuthShell>
            <SignupPage />
          </AuthShell>
        }
      />
      <Route
        path="/reset-password"
        element={
          <AuthShell>
            <ResetPasswordPage />
          </AuthShell>
        }
      />
      {/* E65.F3 (ADR-0069): troca obrigatória da senha provisória. Fora do
          ShellRoute de propósito — sem menu, sem saída lateral: quem chega aqui
          não alcança nenhuma outra superfície até trocar, e o PortalGuard, que
          é quem traz a pessoa para cá, não pode envolver a própria tela sob pena
          de laço. */}
      <Route
        path="/trocar-senha"
        element={
          <AuthGuard>
            <AuthShell bare>
              <TrocarSenhaPage />
            </AuthShell>
          </AuthGuard>
        }
      />
      {/* E93.F4 (GUS-56): o link de cada referência do documento emitido. Fora
          das áreas do perito e do portal de propósito — o mesmo link circula
          entre escritório, banco e plataforma, e quem decide é o recorte do
          acervo no servidor. O AuthGuard guarda o endereço e o login volta. */}
      {/* E145.F2 (GUS-181, ADR-0099 §2.6): a página aberta pelo QR da versão emitida. Fora do
          AuthGuard de propósito — o QR abre sem conta, e a chave vem no fragmento do endereço. */}
      <Route
        path="/acesso"
        element={
          <AuthShell bare>
            <AcessoPorQrPage />
          </AuthShell>
        }
      />
      <Route
        path="/arquivos/:caseId/:pecaId"
        element={
          <AuthGuard>
            <AuthShell bare>
              <ArquivoDaPecaPage />
            </AuthShell>
          </AuthGuard>
        }
      />
      <Route
        path="/onboarding"
        element={
          <AuthGuard>
            <AuthShell bare>
              <OnboardingPage />
            </AuthShell>
          </AuthGuard>
        }
      />

      {/* Home pública (com chrome se logado, mas sem AuthGuard) */}
      <Route
        path="/"
        element={
          <ShellRoute guarded={false}>
            <HomePage />
          </ShellRoute>
        }
      />

      {/* Rotas protegidas (Shell completo + AuthGuard) */}
      <Route
        path="/casos"
        element={
          <ShellRoute>
            <CasosPage />
          </ShellRoute>
        }
      />
      <Route
        path="/casos/novo"
        element={
          <ShellRoute>
            <NovoCasoPage />
          </ShellRoute>
        }
      />
      {/* E78.F1 — resolvedor de destino: carrega o caso e manda para a tela em
          que o trabalho está. Era um placeholder, e a esteira "Criação"
          apontava para ele. */}
      <Route
        path="/casos/:id"
        element={
          <ShellRoute>
            <CasoDestinoPage />
          </ShellRoute>
        }
      />
      {/* E126.F2 (GUS-105): os passos da abertura eletrônica depois do Serviço. */}
      <Route
        path="/casos/:id/abertura"
        element={
          <ShellRoute>
            <AberturaEletronicaPage />
          </ShellRoute>
        }
      />
      <Route
        path="/casos/:id/wizard"
        element={
          <ShellRoute>
            <Suspense fallback={<div data-testid="case-wizard-loading" />}>
              <CaseWizardPage />
            </Suspense>
          </ShellRoute>
        }
      />
      {/* E123.F3 (GUS-128): a etapa de análise fotográfica saiu da esteira — as
          fotos e a conferência facial estão na pré-análise. O endereço antigo
          (favoritos, links do tempo da etapa) leva para lá. */}
      <Route path="/casos/:id/imagens" element={<ParaATelaDoCaso tela="pre-analise" />} />
      {rotaNoShell('/casos/:id/pre-analise', <PreAnalisePage />)}
      <Route
        path="/casos/:id/workspace"
        element={
          <ShellRoute fullBleed>
            <WorkspaceCasoPage />
          </ShellRoute>
        }
      />
      {/* E201.F1 (GUS-47): a mesa única; E205.F4.T05: a ficha da leitura, no "Mais" da mesa. */}
      {rotaNoShell('/casos/:id/mesa', <MesaUnicaPage />)}
      {rotaNoShell('/casos/:id/informacoes-da-leitura', <InformacoesDaLeituraPage />)}
      <Route path="/casos/:id/mesa-eletronica" element={<ParaATelaDoCaso tela="mesa" />} />
      {/* E150.F1 (GUS-150, ADR-0102 §4): os avisos da equipe, do que o cliente registrou. */}
      {rotaNoShell('/avisos', <AvisosDaEquipePage />)}
      {/* E149.F2 (GUS-149, ADR-0102 §3): a conversa do caso com o cliente, da equipe. */}
      {rotaNoShell('/casos/:id/conversa', <ConversaDoCasoPage />)}
      {/* E137.F3 (GUS-160, ADR-0093): o histórico do caso, da equipe. */}
      {rotaNoShell('/casos/:id/historico', <HistoricoDoCasoPage />)}
      {/* E157.F3 (GUS-157): a conferência dos certificados e do carimbo, só da equipe. */}
      {rotaNoShell('/casos/:id/certificados', <CertificadosECarimboPage />)}
      {/* E153.F3 (GUS-176, ADR-0104 §2.6): o resultado auditado do caso, da equipe. */}
      {rotaNoShell('/casos/:id/resultado', <ResultadoDoCasoPage />)}
      {rotaNoShell('/casos/:id/orientacoes', <OrientacoesDoCasoPage />)}
      {rotaNoShell('/casos/:id/saidas', <ConferenciaDasSaidasPage />)}
      {/* E169.F1 (GUS-113/GUS-215): "Conferir e assinar", o destino do passo da assinatura. */}
      {rotaNoShell('/casos/:id/conferencia-final', <ConferenciaFinalPage />)}
      {/* E202.F3 (GUS-145, ADR-0145 §9): o documento se edita na mesa; o editor manual (E171.F3) e a
          correção privada levam para ela, com a saída e o trecho (`?saida=&trecho=`). */}
      <Route path="/casos/:id/editor" element={<ParaATelaDoCaso tela="mesa" />} />
      <Route path="/casos/:id/correcao" element={<ParaATelaDoCaso tela="mesa" />} />
      {rotaNoShell('/casos/:id/laudo', <LaudoCasoPage />)}
      <Route
        path="/analise/:id"
        element={
          <ShellRoute>
            <AnalisePage />
          </ShellRoute>
        }
      />
      <Route
        path="/dossie/:id"
        element={
          <ShellRoute>
            <DossiePage />
          </ShellRoute>
        }
      />
      <Route
        path="/config"
        element={
          <ShellRoute>
            <ConfigPage />
          </ShellRoute>
        }
      />
      <Route
        path="/perfil"
        element={
          <ShellRoute>
            <PerfilPage />
          </ShellRoute>
        }
      />
      <Route
        path="/configuracoes/api-keys"
        element={
          <ShellRoute>
            <ApiKeysPage />
          </ShellRoute>
        }
      />
      <Route
        path="/configuracoes/usuarios"
        element={
          <ShellRoute>
            <MembersPage />
          </ShellRoute>
        }
      />
      <Route
        path="/configuracoes/escritorio"
        element={
          <ShellRoute>
            <EscritorioPage />
          </ShellRoute>
        }
      />
      {/* Certificação (E52.F3, T3): superfície interna da plataforma, gate
          COMPOSTO — escritório-plataforma + papel certificador. */}
      <Route
        path="/certificacao"
        element={
          <ShellRoute>
            <CertificadorRoute>
              <CertificacaoPage />
            </CertificadorRoute>
          </ShellRoute>
        }
      />
      {/* Console de administração (E53.F5, ADR-0064 §4): área própria, com a
          navegação e as rotas condicionadas ao gate de plataforma. */}
      <Route
        path="/admin/clientes"
        element={
          <ShellRoute>
            <PlatformRoute>
              <AdminClientesPage />
            </PlatformRoute>
          </ShellRoute>
        }
      />
      <Route
        path="/admin/usuarios"
        element={
          <ShellRoute>
            <PlatformRoute>
              <AdminUsuariosPage />
            </PlatformRoute>
          </ShellRoute>
        }
      />
      <Route
        path="/admin/trilha"
        element={
          <ShellRoute>
            <PlatformRoute>
              <AdminTrilhaPage />
            </PlatformRoute>
          </ShellRoute>
        }
      />
      <Route
        path="/configuracoes/precificacao"
        element={
          <ShellRoute>
            <PlatformRoute>
              <PrecificacaoPage />
            </PlatformRoute>
          </ShellRoute>
        }
      />
      {/* Portal do Cliente (E50.F2) — chunk lazy; guards vivem dentro de
          PortalRoutes (AuthGuard > PortalGuard > PortalShell). */}
      <Route
        path="/portal/*"
        element={
          <Suspense fallback={<div data-testid="portal-loading" />}>
            <PortalRoutes />
          </Suspense>
        }
      />

      {/* Páginas de erro navegacionais (E13.F2.T08) — úteis para deep-link
          e para o smoke E2E exercitar Forbidden/ServerError sem precisar
          provocar um throw real. */}
      <Route
        path="/403"
        element={
          <ShellRoute guarded={false}>
            <ForbiddenPage />
          </ShellRoute>
        }
      />
      <Route
        path="/500"
        element={
          <ShellRoute guarded={false}>
            <ServerErrorPage />
          </ShellRoute>
        }
      />
      <Route
        path="*"
        element={
          <ShellRoute guarded={false}>
            <NotFoundPage />
          </ShellRoute>
        }
      />
    </Routes>
  );
}
